/* global BigInt */
const id = (value) => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
const timestamp = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));

export function validCardAttempt(value, amountCents = Number.MAX_SAFE_INTEGER) {
  return Boolean(
    value &&
      [
        'processing',
        'pending',
        'funded',
        'unknown',
        'declined',
        'failed',
        'returned',
        'refund_pending',
      ].includes(value.state) &&
      (value.providerStatus === null ||
        ['AUTHORIZED', 'CAPTURED', 'SETTLED', 'DECLINED', 'CANCELLED', 'REFUNDED'].includes(
          value.providerStatus
        )) &&
      (value.checkedAt === null || timestamp(value.checkedAt)) &&
      (value.providerStatus === null ? value.checkedAt === null : timestamp(value.checkedAt)) &&
      (value.state !== 'processing' || value.providerStatus === null) &&
      (value.state !== 'funded' || value.providerStatus === 'SETTLED') &&
      (value.state !== 'pending' || ['AUTHORIZED', 'CAPTURED'].includes(value.providerStatus)) &&
      (value.state !== 'declined' ||
        value.providerStatus === null ||
        value.providerStatus === 'DECLINED') &&
      (value.state !== 'failed' || value.providerStatus === null) &&
      (value.adverseRefundAmountCents === undefined ||
        (Number.isSafeInteger(value.adverseRefundAmountCents) &&
          value.adverseRefundAmountCents > 0 &&
          value.adverseRefundAmountCents <= amountCents &&
          ['refund_pending', 'returned', 'unknown'].includes(value.state))) &&
      (value.state !== 'refund_pending' ||
        (value.adverseRefundAmountCents > 0 && value.providerStatus !== null)) &&
      (value.state !== 'returned' ||
        ['CANCELLED', 'REFUNDED'].includes(value.providerStatus) ||
        (value.providerStatus === 'SETTLED' && value.returnedAmountCents > 0)) &&
      (value.returnedAmountCents === undefined ||
        (Number.isSafeInteger(value.returnedAmountCents) &&
          value.returnedAmountCents > 0 &&
          value.returnedAmountCents <= amountCents &&
          value.state === 'returned'))
  );
}

export function validAchAttempt(value, amountCents = Number.MAX_SAFE_INTEGER) {
  return Boolean(
    value &&
      [
        'processing',
        'pending',
        'funded',
        'unknown',
        'declined',
        'failed',
        'returned',
        'refund_pending',
      ].includes(value.state) &&
      (value.providerStatus === null ||
        ['PENDING', 'SUCCEEDED', 'DECLINED', 'VOIDED', 'REFUNDED'].includes(
          value.providerStatus
        )) &&
      (value.checkedAt === null || timestamp(value.checkedAt)) &&
      (value.providerStatus === null || timestamp(value.checkedAt)) &&
      (value.providerStatus !== null || value.checkedAt === null) &&
      (value.state !== 'processing' ||
        (value.providerStatus === null && value.checkedAt === null)) &&
      (value.state !== 'funded' || value.providerStatus === 'SUCCEEDED') &&
      (value.state !== 'pending' || value.providerStatus === 'PENDING') &&
      (value.adverseRefundAmountCents === undefined ||
        (Number.isSafeInteger(value.adverseRefundAmountCents) &&
          value.adverseRefundAmountCents > 0 &&
          value.adverseRefundAmountCents <= amountCents &&
          ['refund_pending', 'returned', 'unknown'].includes(value.state))) &&
      (value.state !== 'refund_pending' ||
        (value.adverseRefundAmountCents > 0 && value.providerStatus !== null)) &&
      (value.returnedAmountCents === undefined ||
        (Number.isSafeInteger(value.returnedAmountCents) &&
          value.returnedAmountCents > 0 &&
          value.returnedAmountCents <= amountCents &&
          value.state === 'returned')) &&
      (value.state !== 'returned' ||
        ['VOIDED', 'REFUNDED'].includes(value.providerStatus) ||
        (value.providerStatus === 'SUCCEEDED' && value.returnedAmountCents > 0)) &&
      (value.state !== 'declined' ||
        value.providerStatus === null ||
        value.providerStatus === 'DECLINED') &&
      (value.state !== 'failed' || value.providerStatus === null)
  );
}

/** Prepared purchase evidence is a server quote, never a charge or a spendable credit. */
export function validPurchaseQuote(value) {
  return Boolean(
    value &&
      typeof value.familyKey === 'string' &&
      /^[a-z][a-z0-9_]{1,39}$/.test(value.familyKey) &&
      Number.isSafeInteger(value.units) &&
      value.units > 0 &&
      value.units <= 1_000_000_000 &&
      ['wire', 'ach', 'card'].includes(value.method) &&
      value.currency === 'USD' &&
      Number.isSafeInteger(value.ratePerUnitScaled) &&
      value.ratePerUnitScaled > 0 &&
      value.rateScale === 10000 &&
      Number.isSafeInteger(value.amountCents) &&
      value.amountCents > 0 &&
      Number.isSafeInteger(value.policyRevision) &&
      value.policyRevision >= 0 &&
      value.policyRevision < Number.MAX_SAFE_INTEGER &&
      typeof value.priceRevision === 'string' &&
      /^[a-f\d]{64}$/.test(value.priceRevision) &&
      (BigInt(value.units) * BigInt(value.ratePerUnitScaled) + 50n) / 100n ===
        BigInt(value.amountCents)
  );
}

export function readPurchaseReceipt(response) {
  const value = response?.data?.data;
  if (
    !validPurchaseQuote(value) ||
    !id(value._id) ||
    !['prepared', 'credited'].includes(value.status) ||
    (value.creditState !== undefined &&
      value.creditState !== (value.status === 'credited' ? 'credited' : 'uncredited')) ||
    value.paymentsEnabled !== false ||
    !timestamp(value.createdAt) ||
    typeof value.open !== 'boolean' ||
    (value.returnReview !== undefined &&
      (!['ach', 'card'].includes(value.method) ||
        (value.method === 'ach'
          ? !['returned', 'refund_pending', 'unknown'].includes(value.achAttempt?.state)
          : !value.cardAttempt) ||
        !Number.isSafeInteger(value.returnReview?.revision) ||
        value.returnReview.revision < 0 ||
        typeof value.returnReview.open !== 'boolean' ||
        (value.returnReview.open
          ? value.returnReview.resolvedAt !== null
          : !timestamp(value.returnReview.resolvedAt) ||
            Date.parse(value.returnReview.resolvedAt) < Date.parse(value.createdAt)))) ||
    (value.achAttempt != null &&
      (value.method !== 'ach' ||
        (value.status === 'prepared' && !value.open && !isClosableAchDecline(value)) ||
        !validAchAttempt(value.achAttempt, value.amountCents) ||
        (value.achAttempt.checkedAt !== null &&
          Date.parse(value.achAttempt.checkedAt) < Date.parse(value.createdAt)) ||
        (value.status === 'credited' &&
          !['funded', 'unknown', 'returned', 'refund_pending'].includes(
            value.achAttempt.state
          )))) ||
    (value.cardAttempt != null &&
      (value.method !== 'card' ||
        value.achAttempt != null ||
        (value.status === 'prepared' && !value.open && !isClosableCardDecline(value)) ||
        !validCardAttempt(value.cardAttempt, value.amountCents) ||
        (value.cardAttempt.checkedAt !== null &&
          Date.parse(value.cardAttempt.checkedAt) < Date.parse(value.createdAt)) ||
        (value.status === 'credited' &&
          !['funded', 'unknown', 'returned', 'refund_pending'].includes(
            value.cardAttempt.state
          )))) ||
    (value.status === 'prepared' &&
      (value.bankEntryId != null || value.creditedAt != null || value.creditEvidence != null)) ||
    (value.status === 'credited' &&
      (!['wire', 'ach', 'card'].includes(value.method) ||
        (value.method === 'card' &&
          (!value.cardAttempt || value.creditEvidence?.environment !== 'production')) ||
        (value.method === 'ach' &&
          (!value.achAttempt || value.creditEvidence?.environment !== 'production')) ||
        (value.method === 'wire' && value.creditEvidence != null) ||
        value.open ||
        !id(value.bankEntryId) ||
        !timestamp(value.creditedAt) ||
        Date.parse(value.creditedAt) < Date.parse(value.createdAt))) ||
    (value.open
      ? value.closedAt !== null
      : !timestamp(value.closedAt) || Date.parse(value.closedAt) < Date.parse(value.createdAt))
  )
    throw new Error('Invalid purchase draft');
  return value;
}

/** A processor observation and a historical unit credit are separate facts. */
export function purchaseCreditMessage(value) {
  if (value.status !== 'credited')
    return value.achAttempt || value.cardAttempt
      ? 'No bank units added.'
      : 'No payment taken. No bank units added.';
  if (value.method === 'wire') return 'Wire confirmed. Purchased units were added to your bank.';
  if (
    (value.method === 'card' && value.returnReview) ||
    (value.method === 'ach' &&
      (value.achAttempt?.state === 'refund_pending' ||
        (value.returnReview && value.achAttempt?.state === 'unknown')))
  )
    return 'Purchased units were credited previously. The recorded credit is retained during payment review.';
  return (value.achAttempt || value.cardAttempt)?.state === 'returned' ||
    (value.method === 'card' && value.returnReview)
    ? 'Purchased units were credited previously. The recorded credit is retained after this return.'
    : value.method === 'card'
      ? 'Card settled. Purchased units were added to your bank.'
      : 'ACH cleared. Purchased units were added to your bank.';
}

export function purchaseBankStateKey(value) {
  return JSON.stringify([
    value._id,
    value.status,
    value.bankEntryId,
    value.achAttempt?.state,
    value.achAttempt?.returnedAmountCents,
    value.achAttempt?.adverseRefundAmountCents,
    value.cardAttempt?.state,
    value.cardAttempt?.returnedAmountCents,
    value.cardAttempt?.adverseRefundAmountCents,
    value.returnReview?.revision,
    value.returnReview?.open,
    value.returnReview?.resolvedAt,
    value.recovery?.revision,
    value.recovery?.blocked,
    value.recovery?.resolvedAt,
  ]);
}

export function purchaseState(value) {
  if (value.status === 'prepared' && !value.open) {
    if (isClosableAchDecline(value)) return 'ACH declined — purchase closed; no units credited.';
    if (isClosableCardDecline(value)) return 'Card declined — purchase closed; no units credited.';
  }
  if (value.cardAttempt) {
    if (value.cardAttempt.state === 'refund_pending' && value.returnReview?.open !== false)
      return 'Refund under review — review the recorded bank credit separately.';
    if (value.cardAttempt.state === 'returned' || value.returnReview)
      return value.returnReview?.open === false
        ? 'Card return reconciled. Review the recorded bank credit separately.'
        : 'Card refund or dispute recorded — review required.';
    if (value.status === 'credited')
      return value.cardAttempt.state === 'unknown'
        ? 'Units credited — payment status currently unconfirmed.'
        : 'Card settled — units credited';
    return {
      processing: 'Payment request processing — no units credited.',
      pending: 'Card authorized or captured — not settled; no units credited.',
      funded: 'Card payment verified — bank credit pending.',
      unknown: 'Payment outcome unknown — do not submit again.',
      declined: 'Card declined — no units credited.',
      failed: 'Payment unavailable — review required.',
    }[value.cardAttempt.state];
  }
  if (value.achAttempt?.state === 'refund_pending' && value.returnReview?.open !== false)
    return 'Refund under review — review the recorded bank credit separately.';
  if (value.achAttempt?.state !== 'returned' && value.returnReview)
    return value.returnReview.open === false
      ? 'ACH payment review reconciled — recorded credit retained.'
      : 'ACH refund or return recorded — review required.';
  if (value.achAttempt?.state === 'returned' && value.returnReview?.open === false)
    return value.status === 'credited'
      ? 'ACH return reconciled — recorded credit retained.'
      : 'ACH return reconciled — no units credited.';
  if (value.status === 'credited' && value.method === 'ach')
    return value.achAttempt?.state === 'returned'
      ? 'ACH returned — review required; recorded credit retained.'
      : value.achAttempt?.state === 'unknown'
        ? 'Units credited — payment status currently unconfirmed.'
        : 'ACH cleared — units credited';
  if (value.achAttempt)
    return {
      processing: 'Payment request processing — no units credited.',
      pending: 'ACH pending — no units credited.',
      funded: 'ACH payment verified — bank credit pending.',
      unknown: 'Payment outcome unknown — do not submit again.',
      declined: 'ACH declined — no units credited.',
      failed: 'Payment unavailable — review required.',
      returned: 'ACH returned — contact MedEffects.',
    }[value.achAttempt.state];
  return value.status === 'credited'
    ? 'Wire confirmed — units credited'
    : value.open
      ? 'Draft saved — payment not started'
      : 'Draft closed — payment not started';
}

export function samePurchaseQuote(a, b) {
  return [
    'familyKey',
    'units',
    'method',
    'currency',
    'ratePerUnitScaled',
    'rateScale',
    'amountCents',
    'policyRevision',
    'priceRevision',
  ].every((key) => a[key] === b[key]);
}

export const validPurchaseOptions = (value) =>
  value?.paymentsEnabled === false &&
  value.preparationVersion === 1 &&
  Array.isArray(value.families) &&
  value.families.every(
    (row) =>
      typeof row?.familyKey === 'string' &&
      /^[a-z][a-z0-9_]{1,39}$/.test(row.familyKey) &&
      typeof row.label === 'string'
  ) &&
  ['wire', 'ach', 'card'].every((method) => typeof value.paymentMethods?.[method] === 'boolean');

export function isClosableCardDecline(value) {
  return (
    value?.method === 'card' &&
    value.cardAttempt?.state === 'declined' &&
    value.cardAttempt.providerStatus === 'DECLINED' &&
    value.returnReview === undefined &&
    value.cardAttempt.returnedAmountCents === undefined &&
    value.cardAttempt.adverseRefundAmountCents === undefined
  );
}

export function isClosableAchDecline(value) {
  return (
    value?.method === 'ach' &&
    value.achAttempt?.state === 'declined' &&
    value.achAttempt.providerStatus === 'DECLINED' &&
    value.returnReview === undefined &&
    value.achAttempt.returnedAmountCents === undefined &&
    value.achAttempt.adverseRefundAmountCents === undefined
  );
}

/** A closed draft cannot erase a payment attempt observed before closure. */
export function sameClosurePurchase(value, original, paymentLocked = false) {
  if (paymentLocked && !(value.achAttempt || value.cardAttempt)) return false;
  if (!original) return !paymentLocked;
  return (
    value._id === original._id &&
    samePurchaseQuote(value, original) &&
    (!(original.achAttempt || original.cardAttempt) ||
      (value.status === 'prepared' &&
        value.createdAt === original.createdAt &&
        (isClosableCardDecline(value) || isClosableAchDecline(value))))
  );
}

export const isClosablePaymentDecline = (value) =>
  isClosableCardDecline(value) || isClosableAchDecline(value);

export function requireClosureEvidence(value, original, paymentLocked) {
  if (
    !value.open &&
    value.status === 'prepared' &&
    !sameClosurePurchase(value, original, paymentLocked)
  )
    throw new Error('Unconfirmed payment closure');
}
